import { useEffect, useState } from 'react';
import { Badge, Box, Button, Flex, Icon, Spinner, Text } from '@chakra-ui/react';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import { PlanItem } from '@/lib/types';
import { draftFromSlot } from '@/pages/planner/draft';
import { PlannerAction } from '@/pages/planner/planner-machine';
import useCurriculumsStore from '@/utils/use-curriculums';
import { toaster } from '@/components/ui/toaster';
import { useModalValueToggle } from '@/lib/hooks/use-modal-toggle';
import PlannerItemDrawer from '@/pages/planner/item-drawer/planner-item-drawer';
import PlanMetaChips from './plan-meta-chips';
import PlanBody from './plan-body';

interface CurSlotViewScreenProps {
  curriculumId: number;
  slotId: number;
  dispatch: (action: PlannerAction) => void;
}

// "curSlotView" — read-only view of a single built curriculum plan (slot),
// sharing the day-plan view's body layout via plan-body.tsx. Badge
// "Plan N of M", sub-label the curriculum name, actions "← Curriculum" (back
// to curBuild) and "Edit plan" (into the builder, curriculum mode).
export default function CurSlotViewScreen({ curriculumId, slotId, dispatch }: CurSlotViewScreenProps) {
  const items = useCurriculumsStore((s) => s.items);
  const fetchOne = useCurriculumsStore((s) => s.fetchOne);
  const [isLoading, setIsLoading] = useState(true);
  const itemDrawer = useModalValueToggle<PlanItem>();

  const curriculum = items.find((c) => c.id === curriculumId);
  const slot = curriculum?.plans?.find((p) => p.id === slotId);

  useEffect(() => {
    setIsLoading(true);
    fetchOne(curriculumId)
      .catch((error: any) => {
        toaster.create({
          type: 'error',
          title: error.response?.data?.error ?? 'Failed to load curriculum plan',
        });
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curriculumId, slotId]);

  if (isLoading && !slot) {
    return (
      <Flex justify='center' py={10}>
        <Spinner color='fg.subtle' />
      </Flex>
    );
  }

  if (!curriculum || !slot) {
    return (
      <Text fontSize='sm' color='fg.muted'>
        Curriculum plan not found.
      </Text>
    );
  }

  const handleEdit = () => {
    dispatch({
      type: 'EDIT_CURRICULUM_PLAN',
      curriculumId,
      slotId: slot.id,
      curriculumName: curriculum.name,
      sortOrder: slot.sort_order,
      planCount: curriculum.plan_count,
      returnTo: 'curSlotView',
      draft: draftFromSlot(slot, curriculum, '', false),
    });
  };

  return (
    <Box>
      <Flex align='center' justify='space-between' mb={4} flexWrap='wrap' gap={3}>
        <Box>
          <Flex align='center' gap={2} mb={1}>
            <Badge variant='outline' size='sm'>
              Plan {slot.sort_order} of {curriculum.plan_count}
            </Badge>
          </Flex>
          <Text fontFamily='heading' fontWeight='800' fontSize='h2' textTransform='uppercase' color='fg.strong'>
            {slot.name}
          </Text>
          <Text fontSize='sm' color='fg.muted'>
            {curriculum.name}
          </Text>
        </Box>

        <Flex gap={2} flexWrap='wrap'>
          <Button
            variant='outline'
            onClick={() => dispatch({ type: 'GO_TO', screen: { name: 'curBuild', curriculumId } })}
          >
            <Icon as={ArrowLeftIcon} w={4} h={4} />
            Curriculum
          </Button>
          <Button colorPalette='brand' onClick={handleEdit}>
            <Icon as={PencilIcon} w={4} h={4} />
            Edit plan
          </Button>
        </Flex>
      </Flex>

      <Box mb={5}>
        <PlanMetaChips durationMinutes={slot.duration_minutes} />
      </Box>

      {/* PlanBody renders learning outcomes/prerequisites, but a slot no longer
          carries them — they're declared once on the parent curriculum. Merge the
          curriculum's copy in so this view still shows what the slot's plans will
          inherit, rather than loosening PlanBody's contract. */}
      <PlanBody
        plan={{
          ...slot,
          learning_outcomes: curriculum.learning_outcomes,
          prerequisites: curriculum.prerequisites,
        }}
        onViewItem={itemDrawer.open}
      />

      <PlannerItemDrawer isOpen={itemDrawer.isOpen} onClose={itemDrawer.close} item={itemDrawer.state} />
    </Box>
  );
}
